import { useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { ArrowLeft, Truck } from "lucide-react";

export default function ForgotPassword() {
  const { resetPassword } = useAuth();
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    const { error: err } = await resetPassword(email);
    setSubmitting(false);
    if (err) {
      setError(err.message);
      return;
    }
    setSubmitted(true);
  };

  return (
    <div
      data-testid="forgot-password-page"
      className="min-h-screen w-full bg-gradient-to-br from-slate-900 via-slate-800 to-blue-900 flex items-center justify-center px-4 py-12"
    >
      <div className="w-full max-w-md">
        <div className="flex items-center justify-center gap-3 mb-8">
          <div className="rounded-xl bg-white/10 backdrop-blur p-3 ring-1 ring-white/20">
            <Truck className="h-7 w-7 text-white" strokeWidth={1.5} />
          </div>
          <div className="text-white">
            <div className="text-xl font-semibold tracking-tight">timeslot.au</div>
            <div className="text-xs text-white/70 -mt-0.5">Logistics Booking System</div>
          </div>
        </div>

        <Card className="border-white/10 bg-white/95 backdrop-blur shadow-2xl">
          <CardHeader>
            <CardTitle>Reset password</CardTitle>
            <CardDescription>
              We&apos;ll email you a link to choose a new password.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {submitted ? (
              <Alert data-testid="reset-sent-alert">
                <AlertDescription>
                  If an account exists for <strong>{email}</strong>, a reset link is on its way.
                </AlertDescription>
              </Alert>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                {error && (
                  <Alert variant="destructive">
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    data-testid="forgot-email-input"
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full"
                  disabled={submitting}
                  data-testid="forgot-submit-btn"
                >
                  {submitting ? "Sending…" : "Send reset link"}
                </Button>
              </form>
            )}
            <div className="mt-4 text-center">
              <Link
                to="/login"
                className="inline-flex items-center gap-1 text-sm text-blue-600 hover:underline"
                data-testid="back-to-login-link"
              >
                <ArrowLeft className="h-4 w-4" /> Back to sign in
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
